import { getLang } from '@/helpers/getLang'
import { setJwtToken } from '@/helpers/setRequestHeaders'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Manager } from './admin/managersApi'

export type Product = {
    id: number
    title: string
    image: string
    type: number[]
    animal_types_category: any[]
    animal_type: {
        id: number
        title: string
        value: string
        image: string
        tags: []
        icon: string
        translit: string
        animal_types_category: []
    }[]
    feeding_type: number[]
    animal_age: any
    website: string
    animal_needs: any
    subtitle: string
    volume: number
    expiration_date: string
    description: string
    necessary_list: string[]
    manager?: Manager[]
    buttonVariant?: 'Website' | 'Form',
    composition_id: {
        id: number
        title: string
        types: {
            amount: string
            id: number
            title: string
            value: string
        }[]
        value: string
    }[]
    application: string
    application_values: {
        id: number
        title: string
        types: {
            amount: string
            id: number
            title: string
            value: string
        }[]
        value: string
    }[]
    gallery: string[]
    file: string
    videos: number[]
    shelf_life?: string
    volume_string?: string
    composition_text?: string
    views?: number
}

type ProductDto = {
    title: string
    image: string
    type: number[]
    animal_type: number[]
    feeding_type: number[]
    animal_age: number[]
    animal_needs: number[]
    subtitle: string
    volume: number
    expiration_date: string
    description: string
    necessary_list: string[]
    composition_id: number[]
    application: string
    application_values: number[]
    gallery: string[]
    videos: string[]
}

export type ProductFilter = {
    page: number
    count: number
    filters: {
        title: string
        type: number[]
        animal_type: number[]
        animal_age: number[]
        animal_needs: number[]
        feeding_type: number[]
    }
}

export type TypeOfAnimal = {
    id: number
    title: string
    value: string
    image: string
    tags: number[]
    icon: string
    translit: string
    oneCategory: boolean
    animal_types_category: {
        id: number
        title: string
        value: string
        image: string
        icon: string
        translit: string
        animal_type_id: number
    }[]
}

export type CatalogCheckboxType = {
    id: number
    title: string
    value: string
}

export type CatalogCheckboxFilter = {
    title: string
    items: CatalogCheckboxType[]
}

export const catalogApi = createApi({
    reducerPath: 'catalogApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://megakorm.ru:3005/api',
        prepareHeaders: setJwtToken
    }),
    tagTypes: ['PRODUCTS'],
    endpoints: builder => ({
        getAllProducts: builder.query<Product[], { count: number; page: number }>({
            query: ({ count, page }) => ({ url: `/product?get=${count}&page=${page}` }),
            providesTags: ['PRODUCTS']
        }),
        getAllProductsEn: builder.query<Product[], { count: number; page: number }>({
            query: ({ count, page }) => ({ url: `/product?get=${count}&page=${page}&lang=en` }),
            providesTags: ['PRODUCTS']
        }),
        getProduct: builder.query<Product, { id: string }>({
            query: ({ id }) => ({ url: `/product/by-translit/${id}?lang=${getLang()}` }),
            providesTags: ['PRODUCTS']
        }),
        getInitialCategories: builder.query<TypeOfAnimal[], void>({
            query: () => ({ url: `/animal-types?lang=${getLang()}` })
        }),
        getAllCatalogFilters: builder.query<CatalogCheckboxFilter[], { category: number; category_type: number }>({
            query: data => ({
                url: `/product/filters/getAll${data.category ? '?category=' + data.category : ''}${
                    data.category_type ? '&category_type=' + data.category_type : ''
                }`,
                params: { lang: getLang() }
            })
        }),
        createProduct: builder.mutation<any, ProductDto>({
            query: body => ({ url: `/product`, method: 'POST', body: body }),
            extraOptions: { includeToken: true },
            invalidatesTags: ['PRODUCTS']
        }),
        filterProducts: builder.mutation<ProductFilter, any>({
            query: body => ({ url: `/product/get/filtred/pages`, method: 'POST', body: { ...body, lang: getLang() } })
        }),
        addView: builder.mutation<any, number>({
            query: data => ({ url: `/product/add-view`, method: 'POST', body: { id: data } })
        }),
        deleteProductsByIds: builder.mutation<any, any>({
            query: data => ({ url: `/product/delete-by-ids/del`, method: 'POST', body: data }),
            extraOptions: { includeToken: true },
            invalidatesTags: ['PRODUCTS']
        }),
        updateProduct: builder.mutation<any, any>({
            query: data => ({ url: `/product/${data.id}`, method: 'PATCH', body: data }),
            extraOptions: { includeToken: true },
            invalidatesTags: ['PRODUCTS']
        }),
        toggleEnableToViewProduct: builder.mutation<any, { id: number }>({
            query: data => ({ url: '/product/enable-to-view', method: 'POST', body: data }),
            invalidatesTags: ['PRODUCTS']
        }),
        toggleEnableToViewByListProduct: builder.mutation<any, { ids: number[]; enable?: boolean }>({
            query: data => ({ url: '/product/enable-to-view/list', method: 'POST', body: data }),
            invalidatesTags: ['PRODUCTS']
        })
    })
})

export const {
    useAddViewMutation,
    useToggleEnableToViewByListProductMutation,
    useToggleEnableToViewProductMutation,
    useGetAllProductsQuery,
    useGetAllProductsEnQuery,
    useGetAllCatalogFiltersQuery,
    useGetProductQuery,
    useCreateProductMutation,
    useFilterProductsMutation,
    useDeleteProductsByIdsMutation,
    useGetInitialCategoriesQuery,
    useUpdateProductMutation
} = catalogApi
